import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import Card from '../dish-menu-creator/Card';
import { SubFunctionAuxiliaryHint } from '../rbp-cloud-sub-function/sub-function-auxiliary-hint.component';
import BaseInput from '../../component/form/base-input.component';
import { apiManage_rbp } from '../../request';
import _ from 'lodash';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import { CardToolbarIconButton } from '../../component/button/card-toolbar-icon-button.component';
import DialogComponent from '../../component/dialog/dialog.component';
import DialogFormContentSurface, { getDialogFormBodyContentSx } from '../../component/dialog/dialog-form-content-surface.component';

/**
 * 文字就餐指南：首屏按 Figma 展示为多行正文，点击编辑在弹窗内修改并保存。
 */
export default class BulletinComponent extends React.Component<Props, State> {
    /** 已发布的指南正文 */
    public state: Readonly<State> = {
        value: '',
        editOpen: false,
        draftValue: '',
        /** 弹窗「确定」提交中 */
        saveLoading: false,
    };

    constructor(props: Props) {
        super(props);
    }

    componentDidMount() {
        void this.init();
    }

    /** 拉取当前文字就餐指南 */
    async init() {
        const result = await apiManage_rbp.get_bulletin();
        if (!_.get(result, 'error')) {
            this.setState({
                value: _.get(result, '[0].value', '') || '',
            });
        }
    }

    /** 将正文提交到后端（弹窗「确定」触发），保存按钮展示公共 loading */
    async persistBulletin() {
        this.setState({ saveLoading: true });
        try {
            const result = await apiManage_rbp.patch_bulletin(this.state.draftValue);
            if (!_.get(result, 'error')) {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                this.setState({
                    value: this.state.draftValue,
                    editOpen: false,
                });
            } else {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            }
        } finally {
            this.setState({ saveLoading: false });
        }
    }

    /** 打开编辑弹窗并带入当前正文副本 */
    openEditDialog() {
        this.setState({
            editOpen: true,
            draftValue: this.state.value,
            saveLoading: false,
        });
    }

    /** 弹窗内输入变更 */
    onDraftChange(value: string) {
        this.setState({ draftValue: value });
    }

    render() {
        const auxiliaryModeEnabled = this.props.auxiliaryModeEnabled === true;
        const { value, editOpen, draftValue, saveLoading } = this.state;
        const displayText = value.trim() ? value : i18n.t('google_text_public_text_no_data');

        return (
            <>
                <Card
                    className="common-card"
                    title={(
                        <Box sx={{ width: '100%' }}>
                            <Typography variant="subtitle1" sx={{ color: 'var(--text-primary-300)', fontWeight: 700 }}>
                                {i18n.t('first_screen_setting_page_text_meal_guide')}
                            </Typography>
                            <SubFunctionAuxiliaryHint enabled={auxiliaryModeEnabled} i18nKey="first_screen_aux_text_meal_guide" />
                        </Box>
                    )}
                    actionRender={(
                        <CardToolbarIconButton
                            kind="edit"
                            ariaLabel={i18n.t('general_text_edit')}
                            onClick={() => {
                                this.openEditDialog();
                            }}
                        />
                    )}
                    contentRender={(
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                            }}
                        >
                            <Box
                                sx={{
                                    ...(!value.trim()
                                        ? {
                                            flex: '1 1 0',
                                            minHeight: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            textAlign: 'center',
                                        }
                                        : {
                                            display: 'block',
                                            width: '100%',
                                        }),
                                }}
                            >
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: 16,
                                        fontWeight: 500,
                                        lineHeight: '24px',
                                        color: value.trim() ? 'var(--text-dark)' : 'var(--text-primary-500)',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        textAlign: value.trim() ? 'left' : 'center',
                                    }}
                                >
                                    {displayText}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                />

                <DialogComponent
                    maxWidth="sm"
                    heightSize="small"
                    sx={getDialogFormBodyContentSx('start')}
                    dialogContentStyle={{ paddingTop: 0 }}
                    open={editOpen}
                    onClose={() => {
                        this.setState({ editOpen: false, saveLoading: false });
                    }}
                    defaultHeaderProps={{
                        title: i18n.t('first_screen_setting_page_text_meal_guide'),
                        onClose: () => {
                            this.setState({ editOpen: false, saveLoading: false });
                        },
                    }}
                    footer={(
                        <div>
                            <Button
                                color="primary"
                                variant="outlined"
                                disabled={saveLoading}
                                onClick={() => this.setState({ editOpen: false, saveLoading: false })}
                            >
                                {i18n.t('google_text_public_button_cancel')}
                            </Button>
                            <Box marginLeft={4} display="inline-block">
                                <LoadingOverlayButton
                                    color="primary"
                                    variant="contained"
                                    loading={saveLoading}
                                    loadingIndicatorSize={24}
                                    onClick={() => {
                                        void this.persistBulletin();
                                    }}
                                >
                                    {i18n.t('google_text_confirm')}
                                </LoadingOverlayButton>
                            </Box>
                        </div>
                    )}
                >
                    <DialogFormContentSurface fullHeight>
                        <BaseInput
                            variant="outlined"
                            fullWidth
                            label=""
                            multiline
                            minRows={6}
                            value={draftValue}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                this.onDraftChange(event.target.value);
                            }}
                        />
                    </DialogFormContentSurface>
                </DialogComponent>
            </>
        );
    }
}

type Props = {
    /** 云 RBP 子功能壳顶栏「辅助模式」是否开启 */
    auxiliaryModeEnabled?: boolean;
};

type State = {
    /** 已保存的正文 */
    value: string;
    /** 编辑弹窗是否打开 */
    editOpen: boolean;
    /** 弹窗内编辑中的正文 */
    draftValue: string;
    saveLoading: boolean;
};
